import { ChevronDownIcon, GripVerticalIcon } from "lucide-react";
import React from "react";
import {
  Button,
  Cell,
  type CellProps,
  Collection,
  Column,
  type ColumnProps,
  ColumnResizer as ColumnResizerPrimitive,
  type ColumnResizerProps,
  composeRenderProps,
  ResizableTableContainer,
  Row,
  type RowProps,
  TableBody,
  type TableBodyProps,
  TableHeader,
  type TableHeaderProps,
  Table as TablePrimitive,
  type TableProps as TablePrimitiveProps,
  useTableOptions,
} from "react-aria-components";
import { tv } from "tailwind-variants";

import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/components/ui/utils";

const table = tv({
  slots: {
    root: "**:data-drop-target:outline **:data-drop-target:outline-primary **:data-drop-target:z-10 w-full border-spacing-0 caption-bottom text-sm outline-0",
    header: "border-b",
    row: "group relative cursor-default border-b text-foreground/70 outline-0 selected:bg-accent-subtle selected:hover:bg-accent-subtle/50 dark:selected:hover:bg-accent-subtle/60 hover:bg-accent/50",
    column:
      "whitespace-nowrap relative allows-sorting:cursor-pointer px-3 py-3 text-left dragging:cursor-grabbing font-medium outline-0 [&:has([slot=selection])]:pr-0",
    cell: "group px-3 py-3 outline-0",
    cellIcon:
      "flex-none rounded bg-secondary text-foreground data-[slot=icon]:*:shrink-0 data-[slot=icon]:*:size-3.5 data-[slot=icon]:*:transition-transform data-[slot=icon]:*:duration-200 size-[1.15rem] grid place-content-center shrink-0",
    columnResizer: [
      "touch-none absolute [&[data-resizing]>div]:bg-primary right-0 top-0 bottom-0 w-px px-1 grid place-content-center",
      "data-[resizable-direction=both]:cursor-ew-resize &[data-resizable-direction=left]:cursor-e-resize &[data-resizable-direction=right]:cursor-w-resize",
    ],
  },
  variants: {
    isFocusVisible: {
      true: {
        header: "outline-2 -outline-offset-2 outline-ring/70 rounded-md",
        row: "outline-2 -outline-offset-2 outline-ring/70 rounded-md",
        cell: "outline-2 -outline-offset-2 outline-ring/70 rounded-md",
        column: "outline-2 -outline-offset-2 outline-ring/70 rounded-md",
      },
    },
    isHovered: {
      true: { row: "bg-accent/50" },
    },
    hasLink: {
      true: { row: "cursor-pointer hover:bg-accent/50" },
    },
    allowResize: {
      true: {
        cell: "overflow-hidden truncate",
        column: "overflow-hidden truncate",
      },
    },
  },
});

const { root, header, row, column, cell, cellIcon, columnResizer } = table();

interface TableProps extends TablePrimitiveProps {
  className?: string;
  allowResize?: boolean;
  ref?: React.Ref<HTMLTableElement>;
}

const TableContext = React.createContext<TableProps>({
  allowResize: false,
});

const useTableContext = () => React.useContext(TableContext);

const Table = ({ children, className, ref, ...props }: TableProps) => (
  <TableContext.Provider value={props}>
    {props.allowResize ? (
      <ResizableTableContainer className="overflow-auto">
        <TablePrimitive {...props} className={root({ className })}>
          {children}
        </TablePrimitive>
      </ResizableTableContainer>
    ) : (
      <TablePrimitive {...props} className={root({ className })} ref={ref}>
        {children}
      </TablePrimitive>
    )}
  </TableContext.Provider>
);

const ColumnResizer = ({ className, ...props }: ColumnResizerProps) => (
  <ColumnResizerPrimitive
    {...props}
    className={composeRenderProps(className, (className, renderProps) =>
      columnResizer({
        ...renderProps,
        className,
      }),
    )}
  >
    <div className="bg-border h-full w-px py-3" />
  </ColumnResizerPrimitive>
);

const Body = <T extends object>(props: TableBodyProps<T>) => (
  <TableBody {...props} className={cn("[&_tr:last-child]:border-0")} />
);

interface TableCellProps extends CellProps {
  className?: string;
}

const TableCell = ({ children, className, ...props }: TableCellProps) => {
  const { allowResize } = useTableContext();
  return (
    <Cell
      {...props}
      className={(renderProps) =>
        cell({
          ...renderProps,
          allowResize,
          className,
        })
      }
    >
      {children}
    </Cell>
  );
};

interface TableColumnProps extends ColumnProps {
  className?: string;
  isResizable?: boolean;
}

const TableColumn = ({
  isResizable = false,
  className,
  ...props
}: TableColumnProps) => {
  return (
    <Column
      {...props}
      className={(renderProps) =>
        column({
          ...renderProps,
          allowResize: isResizable,
          className,
        })
      }
    >
      {({ allowsSorting, sortDirection, isHovered }) => (
        <div className="flex data-[slot=icon]:**:shrink-0 items-center gap-2">
          {props.children as React.ReactNode}
          {allowsSorting && (
            <span
              className={cellIcon({
                className: isHovered ? "bg-secondary-foreground/10" : "",
              })}
            >
              <ChevronDownIcon
                data-slot="icon"
                className={sortDirection === "ascending" ? "rotate-180" : ""}
              />
            </span>
          )}
          {isResizable && <ColumnResizer />}
        </div>
      )}
    </Column>
  );
};

interface HeaderProps<T extends object> extends TableHeaderProps<T> {
  className?: string;
}

const Header = <T extends object>({
  children,
  className,
  columns,
  ...props
}: HeaderProps<T>) => {
  const { selectionBehavior, selectionMode, allowsDragging } =
    useTableOptions();
  return (
    <TableHeader {...props} className={header({ className })}>
      {allowsDragging && <Column className="w-0" />}
      {selectionBehavior === "toggle" && (
        <Column className="pl-4 h-full" width={40}>
          {selectionMode === "multiple" && (
            <div className="flex flex-col justify-center h-full">
              <Checkbox slot="selection" className="align-middle" />
            </div>
          )}
        </Column>
      )}
      <Collection items={columns}>{children}</Collection>
    </TableHeader>
  );
};

interface TableRowProps<T extends object> extends RowProps<T> {
  className?: string;
}

const TableRow = <T extends object>({
  children,
  className,
  columns,
  id,
  ...props
}: TableRowProps<T>) => {
  const { selectionBehavior, allowsDragging } = useTableOptions();
  return (
    <Row
      id={id}
      {...props}
      className={(renderProps) =>
        row({
          ...renderProps,
          hasLink: "href" in props,
          className,
        })
      }
    >
      {allowsDragging && (
        <Cell className="ring-primary pr-0 group cursor-grab dragging:cursor-grabbing">
          <Button
            className="relative bg-transparent pl-3.5 py-1.5 text-muted-foreground pressed:text-foreground"
            slot="drag"
          >
            <GripVerticalIcon data-slot="icon" className="size-4" />
          </Button>
        </Cell>
      )}
      {selectionBehavior === "toggle" && (
        <Cell className="pl-4 h-full">
          <span
            aria-hidden
            className="absolute inset-y-0 left-0 hidden h-full w-0.5 bg-primary group-selected:block"
          />
          <div className="flex flex-col justify-center h-full">
            <Checkbox slot="selection" />
          </div>
        </Cell>
      )}
      <Collection items={columns}>{children}</Collection>
    </Row>
  );
};

Table.Body = Body;
Table.Cell = TableCell;
Table.Column = TableColumn;
Table.Header = Header;
Table.Row = TableRow;

export { Table as TableRAC };
